import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/**
 * Gallery videos start playing as soon as their frame is scrolled - or clicked - into view, and
 * pause again when it leaves. They are muted, which is what lets a browser play them unprompted.
 *
 * What gets observed is the frame, not the deferred-media inside it: in a carousel the frame is
 * the slideshow-slide, which carries `content-visibility: auto`, so its descendants are not
 * always laid out and their geometry cannot be trusted.
 *
 * Everything here stays on the progressive mp4. Shopify also publishes an HLS stream, which starts
 * quicker on a phone, but Safari tears its pipeline down at the end of a stream in order to loop -
 * that shows as a stutter and a flash of the poster, and restarting early to dodge it only trades
 * the stutter for a clipped ending. The mp4 loops with no help at all, so the start is made quick
 * by sending a smaller file rather than a different format.
 */

/** Fraction of the frame that has to be showing before the video starts. */
const AUTOPLAY_IN = 0.5;

/** And the fraction it has to fall below before it stops, so a frame parked on the boundary
 * does not flicker between the two. */
const AUTOPLAY_OUT = 0.25;

/**
 * Shopify's transcode ladder, which is fixed - two unrelated uploads of different lengths came back
 * carrying these exact tokens, so they can be relied on rather than looked up. Every rendition sits
 * at the same path as the one the theme renders, with only this part of the filename differing.
 */
const RENDITIONS = [
  { width: 480, token: 'SD-480p-1.5Mbps' },
  { width: 720, token: 'HD-720p-4.5Mbps' },
  { width: 1080, token: 'HD-1080p-7.2Mbps' },
];

/** @type {WeakMap<Element, HTMLElement>} frame -> the deferred-media it holds */
const autoplayFrames = new WeakMap();

/** @type {IntersectionObserver | undefined} */
let autoplayObserver;

/** Anyone who asked for less motion, or for less data, keeps the play button instead. */
function autoplayAllowed() {
  return (
    !matchMedia('(prefers-reduced-motion: reduce)').matches && navigator.connection?.saveData !== true
  );
}

/**
 * Points anything smaller than a desktop at a smaller cut of the same video.
 *
 * `video_tag` only ever emits the 1080p file, and for a 21 second clip that is 18.5MB - which is
 * what a phone was sitting there buffering before anything moved. The 720p cut is 11.8MB and holds
 * up well on a phone screen; 480p is lighter still at 4.2MB but visibly soft once it is stretched
 * across a modern handset, so it is mapped here without being chosen. The original source is left
 * in place behind the rewritten one, so if a rewrite ever misses, the browser falls through to the
 * file that is certain to exist.
 *
 * @param {any} media - The deferred-media element, before its template has been stamped.
 */
function useSmallerRendition(media) {
  const original = media.querySelector('template')?.content.querySelector('source');
  const source = original?.getAttribute('src');

  if (!source) return;

  const current = RENDITIONS.find((rendition) => source.includes(rendition.token));
  const wanted = window.innerWidth < 1200 ? 720 : 1080;
  const target = RENDITIONS.find((rendition) => rendition.width === wanted);

  if (!current || !target || target.width >= current.width) return;

  const smaller = document.createElement('source');
  smaller.setAttribute('src', source.replace(current.token, target.token));
  smaller.setAttribute('type', 'video/mp4');
  original.before(smaller);
}

/**
 * Wires up a video the moment it is stamped in.
 *
 * Safari accepts play() on a video that is holding no data yet and then sits on the poster until
 * something - a scroll, a tap - makes it look again. That is the "I had to scroll to get it going"
 * symptom, so ask again the moment there is anything to play.
 *
 * @param {any} media - The deferred-media element.
 * @param {HTMLVideoElement} video - The video it just stamped in.
 */
function watchPlayback(media, video) {
  video.preload = 'auto';

  for (const event of ['loadeddata', 'canplay']) {
    video.addEventListener(event, () => requestPlay(media, video));
  }

  video.addEventListener('playing', () => {
    media.isPlaying = true;
    media.querySelector('[ref="deferredMediaPlayButton"]')?.classList.add('deferred-media__playing');
    media.updatePlayPauseHint?.(true);
  });
}

/**
 * @param {any} media - The deferred-media element.
 * @param {HTMLVideoElement} video - Its video.
 */
function requestPlay(media, video) {
  if (media.dataset.autoplayWanted !== 'true' || !video.paused) return;

  media.isPlaying = true;
  media.updatePlayPauseHint?.(true);

  video.play()?.catch(() => {
    // A flat refusal - iOS does this in Low Power Mode however muted the video is. Hand the poster
    // button back so there is something to tap. If the data simply had not arrived yet, the canplay
    // retry starts it and the 'playing' handler puts the poster away again.
    media.isPlaying = false;
    media.querySelector('[ref="deferredMediaPlayButton"]')?.classList.remove('deferred-media__playing');
    media.updatePlayPauseHint?.(false);
  });
}

/**
 * Starts a deferred-media element playing.
 * @param {any} media - The deferred-media element.
 */
function startMedia(media) {
  media.dataset.autoplayWanted = 'true';

  if (!media.getAttribute('data-media-loaded')) {
    useSmallerRendition(media);

    // `false` keeps the stamped content from taking focus, which would scroll the page out from
    // under whoever is reading it.
    media.loadContent(false);

    const stamped = media.querySelector('video');
    if (stamped) watchPlayback(media, stamped);
  }

  const video = media.querySelector('video');

  // YouTube and Vimeo are driven over postMessage instead.
  if (!video) {
    media.playMedia();
    return;
  }

  video.muted = true;
  requestPlay(media, video);
}

/** @param {IntersectionObserverEntry[]} entries */
function handleAutoplay(entries) {
  for (const entry of entries) {
    const media = autoplayFrames.get(entry.target);

    if (!media?.isConnected) continue;

    // A frame taller than the window can never show AUTOPLAY_IN of itself, so covering half the
    // screen counts as being in view too.
    const viewport = entry.rootBounds?.height || window.innerHeight;
    const fillsScreen = entry.intersectionRect.height >= viewport * AUTOPLAY_IN;
    const showing = entry.isIntersecting ? entry.intersectionRatio : 0;

    if (entry.isIntersecting && (showing >= AUTOPLAY_IN || fillsScreen)) {
      startMedia(media);
    } else if (showing < AUTOPLAY_OUT && !fillsScreen && media.getAttribute('data-media-loaded')) {
      // Clear the flag first, or a canplay landing after the frame has gone restarts it offscreen.
      media.dataset.autoplayWanted = '';
      media.pauseMedia();
    }
  }
}

/** Watches every gallery video in the document. Safe to call again at any point. */
function watchGalleryVideos() {
  if (!autoplayAllowed()) return;

  for (const media of document.querySelectorAll('media-gallery deferred-media')) {
    const playable =
      media.querySelector('video, iframe') ??
      media.querySelector('template')?.content.querySelector('video, iframe');

    if (!playable) continue;

    const frame = media.closest('.product-media-container') ?? media;

    if (autoplayFrames.get(frame) === media) continue;

    autoplayFrames.set(frame, media);
    autoplayObserver ??= new IntersectionObserver(handleAutoplay, {
      threshold: [0, AUTOPLAY_OUT, AUTOPLAY_IN, 0.75, 1],
    });
    autoplayObserver.observe(frame);
  }
}

customElements.whenDefined('deferred-media').then(() => {
  watchGalleryVideos();

  // A variant change swaps in a whole new media-gallery, so the frames have to be found again.
  document.addEventListener(ThemeEvents.variantUpdate, () => requestAnimationFrame(watchGalleryVideos));
});